/**
 * tRPC client for the platform-admin surface.
 *
 * The Vite dev server proxies `/trpc → http://localhost:5050/trpc/platform`,
 * so on this app the bare `/trpc` URL hits the platform router. Tokens are
 * stored under `edupra_platform_token` and sent as a Bearer header.
 */

import { createTRPCReact, type CreateTRPCReact } from '@trpc/react-query';
import { createTRPCClient as createTRPCStandaloneClient, httpBatchLink } from '@trpc/client';
import superjson from 'superjson';
import type { PlatformRouter } from '@edupra/api';

const PLATFORM_TOKEN_KEY = 'edupra_platform_token';

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(PLATFORM_TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// React Query hooks — used inside components.
export const trpc: CreateTRPCReact<PlatformRouter, unknown> =
  createTRPCReact<PlatformRouter>();

export function createTRPCClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: '/trpc',
        transformer: superjson,
        headers: authHeaders,
      }),
    ],
  });
}

// Imperative proxy client — used outside React (e.g. login flow that needs
// to call mutations sequentially without binding to component state, and
// the `auth.me` ping at boot).
export const platformTRPCProxy = createTRPCStandaloneClient<PlatformRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      transformer: superjson,
      headers: authHeaders,
    }),
  ],
});
